import { FLOWCODE_ROOT } from '@dtx-company/true-common/src/constants/root'
import {
  FRONTEGG_AUTH_HEADER,
  FRONTEGG_SOCIALS_POST_LOGIN_ENDPOINT,
  JSON_HEADERS
} from './constants'
import { FronteggLoginResponse } from './types'
import { addUserToCohort } from './addUserToCohort'
import { logger } from '@dtx-company/logger'
import { notifyObserversOfLogin } from './notify'
import { setAccessTokenCookie } from './setAccessTokenCookie'

export async function socialLogin({
  provider,
  action,
  redirectTo
}: {
  provider: string
  action: string
  redirectTo?: string
  postLoginCallback?: () => Promise<void>
}): Promise<string | void> {
  try {
    const resp = await fetch(`${FLOWCODE_ROOT}/api/frontegg/social-login`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        provider,
        action,
        redirectTo
      })
    })

    if (!resp.ok) {
      await logger.logFetchError(resp, { technicalArea: 'authentication' })
    }

    const { url } = await resp.json()

    return url
  } catch (e: unknown) {
    logger.logError(e, { technicalArea: 'authentication' })
  }
}

export interface SocialPostLoginArgs {
  provider: 'google' | 'facebook'
  code: string
  state: string
  cohort?: string
}

export async function socialPostLogin(
  {
    provider,
    code,
    state,
    cohort
  }: {
    provider: 'google' | 'facebook'
    code: string
    state: string
    cohort?: string
  },
  shouldNotify = true
): Promise<{ redirectUrl?: Location; token?: string }> {
  try {
    const endpoint = new URL(FRONTEGG_SOCIALS_POST_LOGIN_ENDPOINT[provider])

    endpoint.searchParams.append('state', state)
    endpoint.searchParams.append('code', code)

    const resp = await fetch(endpoint, {
      method: 'POST',
      credentials: 'include',
      headers: FRONTEGG_AUTH_HEADER
    })

    //frontegg will return a 302 redirect if google domain is SSO enabled
    if (resp.status === 302) {
      const redirectUrl = (await resp.text()) as unknown as Location
      return { redirectUrl }
    } else if (!resp.ok) {
      await logger.logFetchError(resp, { technicalArea: 'authentication' })
    } else {
      const data: FronteggLoginResponse = await resp.json()
      const decodedState = JSON.parse(state)

      if (cohort && decodedState.action === 'signup') {
        await addUserToCohort(data.accessToken, cohort)
      }
      await setAccessTokenCookie(data.accessToken)
      if (shouldNotify) {
        notifyObserversOfLogin(data.accessToken)
      }
      return { token: data.accessToken }
    }
  } catch (e: unknown) {
    logger.logError(e, { technicalArea: 'authentication' })
  }
  return {}
}
